import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import api from '../services/api';

const ContactSection = ({ isPage = false }) => {

    const [courses, setCourses] = useState([]);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        mobile: '',
        email: '',
        preference: 'Online', // Default to Online
        collegeOrCompany: '',
        address: '',
        trainingInterest: '',
        message: ''
    });

    const [loading, setLoading] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const response = await api.get('/programs');
                setCourses(response.data || []);
            } catch (error) {
                console.error("Failed to fetch programs for dropdown:", error);
            }
        };

        fetchCourses();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Mobile number validation: only allow digits and max 10 characters
        if (name === 'mobile') {
            const numericValue = value.replace(/\D/g, '').slice(0, 10);
            setFormData(prevState => ({
                ...prevState,
                [name]: numericValue
            }));
            return;
        }

        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSubmitStatus(null);

        try {
            await api.post('/contacts', formData);
            setSubmitStatus('success');
            setFormData({
                firstName: '',
                lastName: '',
                mobile: '',
                email: '',
                preference: 'Online',
                collegeOrCompany: '',
                address: '',
                trainingInterest: '',
                message: ''
            });
            setTimeout(() => setSubmitStatus(null), 5000); // Clear success message after 5s
        } catch (error) {
            console.error("Error submitting contact form:", error);
            setSubmitStatus('error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`w-full font-sans relative overflow-hidden ${isPage ? 'pt-12 pb-0 px-0' : 'py-12 px-4'}`}>


            {/* Decorative Elements */}
            <div className="absolute top-0 left-0 w-64 h-64 bg-brand-bg rounded-full mix-blend-multiply filter blur-3xl opacity-30 -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-brand-bg rounded-full mix-blend-multiply filter blur-3xl opacity-30 translate-x-1/2 translate-y-1/2"></div>

            <div className={`${isPage ? 'w-full px-4' : 'max-w-7xl mx-auto'} relative z-10`}>
                {/* Header */}
                <div className="text-center mb-10 md:mb-16">
                    <h2 className="text-2xl md:text-3xl font-bold text-brand-textMuted mb-4 tracking-tight">
                        Contact our team
                        <br />
                        about Our Presence.
                    </h2>
                    <p className="text-sm md:text-base text-brand-gold max-w-2xl mx-auto font-medium">
                        Got any questions? We're here to help.
                    </p>
                </div>

                <div className="bg-brand-card rounded-3xl border border-gray-800 shadow-xl shadow-gray-200/50 overflow-hidden flex flex-col lg:flex-row">
                    {/* Left Side - Form */}
                    <div className="w-full lg:w-1/2 p-8 md:p-12 order-2 lg:order-1">
                        <form className="space-y-5" onSubmit={handleSubmit}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <label htmlFor="firstName" className="block text-xs font-bold text-brand-textLight mb-1.5 uppercase tracking-wide">First name</label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        id="firstName"
                                        placeholder="First name"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all text-brand-textLight placeholder-gray-400 bg-brand-bg hover:bg-brand-card"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="lastName" className="block text-xs font-bold text-brand-textLight mb-1.5 uppercase tracking-wide">Last name</label>
                                    <input
                                        type="text"
                                        name="lastName"
                                        id="lastName"
                                        placeholder="Last name"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all text-brand-textLight placeholder-gray-400 bg-brand-bg hover:bg-brand-card"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <label htmlFor="mobile" className="block text-xs font-bold text-brand-textLight mb-1.5 uppercase tracking-wide">Mobile No</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 flex items-center pl-4 border-r border-gray-800 pr-3 pointer-events-none">
                                            <span className="text-brand-textMuted text-xs font-bold">+91</span>
                                        </div>
                                        <input
                                            type="tel"
                                            name="mobile"
                                            id="mobile"
                                            placeholder="9876543210"
                                            value={formData.mobile}
                                            onChange={handleChange}
                                            className="w-full pl-16 pr-4 py-2.5 text-sm rounded-lg border border-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all text-brand-textLight placeholder-gray-400 bg-brand-bg hover:bg-brand-card"
                                            pattern="[0-9]{10}"
                                            title="Please enter a valid 10-digit mobile number"
                                            required
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="email" className="block text-xs font-bold text-brand-textLight mb-1.5 uppercase tracking-wide">Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        id="email"
                                        placeholder="you@company.com"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all text-brand-textLight placeholder-gray-400 bg-brand-bg hover:bg-brand-card"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <label htmlFor="trainingInterest" className="block text-xs font-bold text-brand-textLight mb-1.5 uppercase tracking-wide">Training Interest</label>
                                    <div className="relative">
                                        <select
                                            name="trainingInterest"
                                            id="trainingInterest"
                                            value={formData.trainingInterest}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2.5 text-sm rounded-lg border border-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all text-brand-textLight bg-brand-bg hover:bg-brand-card appearance-none cursor-pointer"
                                            required
                                        >
                                            <option value="" disabled>Select a course</option>
                                            {courses.map((course) => (
                                                <option key={course._id || course.id} value={course.title}>
                                                    {course.title}
                                                </option>
                                            ))}
                                            <option value="Other">Other</option>
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                                            <ChevronDown className="w-4 h-4 text-gray-400" />
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="preference" className="block text-xs font-bold text-brand-textLight mb-1.5 uppercase tracking-wide">Preference</label>
                                    <div className="relative">
                                        <select
                                            name="preference"
                                            id="preference"
                                            value={formData.preference}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2.5 text-sm rounded-lg border border-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all text-brand-textLight bg-brand-bg hover:bg-brand-card appearance-none cursor-pointer"
                                        >
                                            <option value="Online">Online</option>
                                            <option value="Offline">Offline</option>
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                                            <ChevronDown className="w-4 h-4 text-gray-400" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label htmlFor="collegeOrCompany" className="block text-xs font-bold text-brand-textLight mb-1.5 uppercase tracking-wide">College / Company Name</label>
                                <input
                                    type="text"
                                    name="collegeOrCompany"
                                    id="collegeOrCompany"
                                    placeholder="Enter your college or company name"
                                    value={formData.collegeOrCompany}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all text-brand-textLight placeholder-gray-400 bg-brand-bg hover:bg-brand-card"
                                />
                            </div>

                            <div>
                                <label htmlFor="address" className="block text-xs font-bold text-brand-textLight mb-1.5 uppercase tracking-wide">Address</label>
                                <textarea
                                    name="address"
                                    id="address"
                                    rows={2}
                                    placeholder="Your address..."
                                    value={formData.address}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all text-brand-textLight placeholder-gray-400 bg-brand-bg hover:bg-brand-card resize-none"
                                ></textarea>
                            </div>

                            <div>
                                <label htmlFor="message" className="block text-xs font-bold text-brand-textLight mb-1.5 uppercase tracking-wide">Message (Optional)</label>
                                <textarea
                                    name="message"
                                    id="message"
                                    rows={3}
                                    placeholder="Leave us a message..."
                                    value={formData.message}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all text-brand-textLight placeholder-gray-400 bg-brand-bg hover:bg-brand-card resize-none"
                                ></textarea>
                            </div>

                            {submitStatus === 'success' && (
                                <div className="text-green-400 text-sm font-medium bg-green-900/30 p-3 rounded-lg border border-green-800 mb-4">
                                    Thank you! Your message has been sent successfully.
                                </div>
                            )}
                            {submitStatus === 'error' && (
                                <div className="text-red-400 text-sm font-medium bg-red-900/30 p-3 rounded-lg border border-red-800 mb-4">
                                    Something went wrong. Please try again later.
                                </div>
                            )}

                            <div className="pt-2">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`w-full bg-brand-gold text-black font-bold py-3 px-6 rounded-xl hover:bg-brand-goldHover transition-all transform hover:scale-[1.01] shadow-lg shadow-brand-gold/20 text-sm ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                                >
                                    {loading ? 'Sending...' : 'Send message'}
                                </button>
                            </div>

                        </form>
                    </div>

                    {/* Right Side - Image */}
                    <div className="w-full lg:w-1/2 order-1 lg:order-2 relative min-h-[300px] lg:min-h-full">
                        <img
                            src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80"
                            alt="Contact Us Team"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent"></div>
                        <div className="absolute bottom-6 left-6 right-6 text-white p-4">
                            <h3 className="text-xl font-bold mb-2">We'd love to hear from you</h3>
                            <p className="text-sm text-gray-100">Our team is always here to chat.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactSection;
